import { useEffect, useLayoutEffect, useRef, useState } from "preact/hooks";
import { summarize, type Media } from "../db";
import { isPdf, renderPdf } from "../docs";
import { ImageEditor } from "./ImageEditor";
import { formatSize, prepareFile, fileExt } from "../media";
import * as S from "../state";
import { Avatar } from "./Avatar";
import { deleting, forwarding, replyTo, sendingFiles, viewer, pinning } from "./chatui";
import { Icon } from "./Icon";
import { engine, toast } from "./Main";
import { downloadMedia, useMediaSrc } from "./mediaSrc";

function useEscape(fn: () => void) {
  useLayoutEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && fn();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
}

/** Mounted once by ChatView; each dialog shows when its signal is set. */
export function ChatDialogs({ jid }: { jid: string }) {
  return (
    <>
      {viewer.value && <Viewer />}
      {forwarding.value && <ForwardDialog />}
      {deleting.value && <DeleteDialog />}
      {pinning.value && <PinDialog />}
      {sendingFiles.value && <MediaSendDialog jid={jid} />}
    </>
  );
}

function Viewer() {
  const m = viewer.value!;
  const close = () => {
    viewer.value = null;
    // View once: gone from this device as soon as it's closed.
    if (m.viewOnce && m.dir === "in") void engine.value?.markViewed(m);
  };
  useEscape(close);
  const media = m.media!;
  const { src, error } = useMediaSrc(media);
  const author = m.dir === "out" ? "You" : S.displayName(m.from);
  return (
    <div class="viewer" role="dialog" aria-label="Media viewer" onClick={close}>
      <header onClick={(e) => e.stopPropagation()}>
        <div>
          <strong>{author}</strong>
          <small>{new Date(m.ts).toLocaleString()}</small>
        </div>
        <div class="viewer-actions">
          {!m.viewOnce && (
            <>
              <button class="icon-btn" onClick={() => void downloadMedia(media).catch((e) => toast(String(e.message ?? e)))} aria-label="Download">
                <Icon name="download" />
              </button>
              <button class="icon-btn" onClick={() => ((forwarding.value = [m]), close())} aria-label="Forward">
                <Icon name="forward" />
              </button>
            </>
          )}
          <button class="icon-btn" onClick={close} aria-label="Close">
            <Icon name="close" />
          </button>
        </div>
      </header>
      <div class="viewer-body" onClick={(e) => e.stopPropagation()}>
        {error ? (
          <p class="error">Couldn't open this file: {error}</p>
        ) : !src ? (
          <p class="muted">Decrypting…</p>
        ) : m.kind === "video" ? (
          <video src={src} controls autoplay />
        ) : m.kind === "file" ? (
          <DocView src={src} media={media} />
        ) : (
          <img src={src} alt="" />
        )}
      </div>
      {m.body && <p class="viewer-caption">{m.body}</p>}
    </div>
  );
}

/** A PDF (every page, rendered with pdf.js) or a text file. */
function DocView({ src, media }: { src: string; media: Media }) {
  const pages = useRef<HTMLDivElement>(null);
  const [st, setSt] = useState<{ text?: string; pages?: number; error?: string }>({});
  useEffect(() => {
    let alive = true;
    let close: (() => void) | undefined;
    (async () => {
      const data = await (await fetch(src)).arrayBuffer();
      if (isPdf(media.mime, media.name)) {
        const host = pages.current!;
        const width = Math.max(240, Math.min(host.clientWidth - 24, 900));
        const r = await renderPdf(data, width, (c, _n, total) => alive && (host.appendChild(c), setSt({ pages: total })), () => alive);
        close = r.close;
        if (!alive) close();
      } else {
        const LIMIT = 512_000;
        setSt({ text: new TextDecoder().decode(data.slice(0, LIMIT)) + (data.byteLength > LIMIT ? "\n…" : "") });
      }
    })().catch((e) => alive && setSt({ error: String((e as Error).message ?? e) }));
    return () => {
      alive = false;
      close?.();
    };
  }, [src]);
  if (st.error) return <p class="error">Couldn't show this document: {st.error}</p>;
  if (st.text !== undefined) return <pre class="doc-text">{st.text}</pre>;
  return (
    <div class="doc-view" aria-label="Document pages">
      {!st.pages && <p class="muted">Opening…</p>}
      <div class="doc-pages" ref={pages} />
    </div>
  );
}

/** How long to pin a message for (like WhatsApp: 24 hours, 7 or 30 days). */
function PinDialog() {
  const m = pinning.value!;
  const [days, setDays] = useState(7);
  const close = () => (pinning.value = null);
  useEscape(close);
  return (
    <div class="modal-backdrop" onClick={close}>
      <div class="modal pin-dialog" role="dialog" aria-label="Pin message" onClick={(e) => e.stopPropagation()}>
        <h2>Choose how long your pin lasts</h2>
        <p class="muted small">You can unpin at any time.</p>
        {[1, 7, 30].map((d) => (
          <label key={d} class="radio-row">
            <input type="radio" name="pin-days" checked={days === d} onChange={() => setDays(d)} aria-label={d === 1 ? "24 hours" : `${d} days`} />
            {d === 1 ? "24 hours" : `${d} days`}
          </label>
        ))}
        <div class="row-buttons">
          <button class="link" onClick={close}>
            Cancel
          </button>
          <button class="primary" onClick={() => (close(), void engine.value?.pinMessage(m, days).then(() => toast("Message pinned")))}>
            Pin
          </button>
        </div>
      </div>
    </div>
  );
}

function ForwardDialog() {
  const msgs = forwarding.value!;
  const [picked, setPicked] = useState<string[]>([]);
  const [q, setQ] = useState("");
  const close = () => (forwarding.value = null);
  useEscape(close);

  // Recent chats first, then contacts without a chat.
  const seen = new Set<string>();
  const targets: string[] = [];
  // Frequently contacted first, then recent chats, then contacts.
  for (const j of S.frequent.value) if (!seen.has(j) && !S.isChannel(j)) (seen.add(j), targets.push(j));
  for (const c of S.chats.value) if (!seen.has(c.jid)) (seen.add(c.jid), targets.push(c.jid));
  for (const c of S.contacts.value) if (!seen.has(c.jid)) (seen.add(c.jid), targets.push(c.jid));
  const f = q.trim().toLowerCase();
  const list = targets.filter((j) => !f || S.displayName(j).toLowerCase().includes(f) || j.includes(f.replace(/\D/g, "") || "\u0000"));

  async function send() {
    close();
    await engine.value?.forward(msgs, picked);
    toast(`Forwarded to ${picked.length} chat${picked.length > 1 ? "s" : ""}`);
  }

  return (
    <div class="modal-backdrop" onClick={close}>
      <div class="modal forward" role="dialog" aria-label="Forward message" onClick={(e) => e.stopPropagation()}>
        <header>
          <button class="icon-btn" onClick={close} aria-label="Close">
            <Icon name="close" />
          </button>
          <h2>Forward message to</h2>
        </header>
        <div class="search">
          <Icon name="search" size={20} />
          <input autoFocus value={q} onInput={(e) => setQ(e.currentTarget.value)} placeholder="Search" aria-label="Search chats" />
        </div>
        <ul class="pick-list">
          {list.map((j) => {
            const on = picked.includes(j);
            return (
              <li key={j} onClick={() => setPicked(on ? picked.filter((x) => x !== j) : picked.length < 5 ? [...picked, j] : picked)}>
                <span class={"check" + (on ? " on" : "")}>{on && <Icon name="check" size={16} />}</span>
                <Avatar url={S.profiles.value.get(j)?.avatar_url} jid={j} name={S.displayName(j)} size={40} />
                <span class="chat-name">{S.displayName(j)}</span>
              </li>
            );
          })}
          {!list.length && <li class="empty">No chats found</li>}
        </ul>
        {picked.length > 0 && (
          <footer class="forward-footer">
            <span class="ellipsis">{picked.map((j) => S.displayName(j)).join(", ")}</span>
            <button class="icon-btn send-fab" onClick={() => void send()} aria-label="Send">
              <Icon name="send" />
            </button>
          </footer>
        )}
      </div>
    </div>
  );
}

function DeleteDialog() {
  const m = deleting.value!;
  const e = engine.value!;
  const close = () => (deleting.value = null);
  useEscape(close);
  const forEveryone = e.canRetract(m);
  return (
    <div class="modal-backdrop" onClick={close}>
      <div class="modal" role="alertdialog" aria-modal="true" onClick={(ev) => ev.stopPropagation()}>
        <p>Delete message?</p>
        <div class="stack-buttons">
          {forEveryone && (
            <button class="link" onClick={() => (close(), void e.retract(m))}>
              Delete for everyone
            </button>
          )}
          <button class="link" onClick={() => (close(), void e.hide(m))}>
            Delete for me
          </button>
          <button class="link" onClick={close}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

function MediaSendDialog({ jid }: { jid: string }) {
  const files = sendingFiles.value!;
  const [caption, setCaption] = useState("");
  const [viewOnce, setViewOnce] = useState(false);
  const [hd, setHd] = useState(false);
  const [at, setAt] = useState(0); // the file shown (and edited)
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [urls, setUrls] = useState<string[]>([]);
  const close = () => (sendingFiles.value = null);
  useEscape(close);

  useEffect(() => {
    const u = files.map((f) => (f.type.startsWith("image/") || f.type.startsWith("video/") ? URL.createObjectURL(f) : ""));
    setUrls(u);
    return () => u.forEach((x) => x && URL.revokeObjectURL(x));
  }, [files]);

  const tooBig = files.find((f) => f.size > 100 * 1024 * 1024);

  async function send() {
    setBusy(true);
    const e = engine.value!;
    const reply = replyTo.value;
    replyTo.value = null;
    try {
      for (let i = 0; i < files.length; i++) {
        const p = await prepareFile(files[i], { hd });
        await e.sendDraft(jid, {
          body: i === 0 ? caption : "",
          kind: p.kind,
          media: p.media,
          replyTo: i === 0 && reply ? { id: reply.id, from: reply.from } : undefined,
          viewOnce: viewOnce && (p.kind === "image" || p.kind === "video"),
        });
      }
      close();
    } catch (err) {
      toast(err instanceof Error ? err.message : String(err));
      setBusy(false);
    }
  }

  const first = files[Math.min(at, files.length - 1)];
  const anyImage = files.some((f) => f.type.startsWith("image/") && f.type !== "image/gif");
  if (editing && first.type.startsWith("image/"))
    return (
      <ImageEditor
        file={first}
        onCancel={() => setEditing(false)}
        onDone={(f) => {
          sendingFiles.value = files.map((x, i) => (i === at ? f : x));
          setEditing(false);
        }}
      />
    );
  return (
    <div class="modal-backdrop" onClick={close}>
      <div class="modal media-send" role="dialog" aria-label="Send files" onClick={(e) => e.stopPropagation()}>
        <header>
          <button class="icon-btn" onClick={close} aria-label="Close">
            <Icon name="close" />
          </button>
          <h2>{files.length > 1 ? `${files.length} files` : first.name}</h2>
        </header>
        <div class="media-send-preview">
          {first.type.startsWith("image/") && first.type !== "image/gif" && (
            <button class="edit-photo" onClick={() => setEditing(true)} aria-label="Edit photo" title="Crop, rotate, draw, add text">
              <Icon name="edit" />
            </button>
          )}
          {first.type.startsWith("image/") && urls[at] ? (
            <img src={urls[at]} alt="" />
          ) : first.type.startsWith("video/") && urls[at] ? (
            <video src={urls[at]} controls muted />
          ) : (
            <div class="doc big">
              <span class="doc-icon">{fileExt(first.name)}</span>
              <div class="doc-info">
                <span class="doc-name">{first.name}</span>
                <small>{formatSize(first.size)}</small>
              </div>
            </div>
          )}
        </div>
        {files.length > 1 && (
          <div class="media-send-strip">
            {files.map((f, i) => (
              <div key={i} class={"thumb" + (i === at ? " on" : "")} onClick={() => setAt(i)} role="button" aria-label={`Show ${f.name}`}>
                {urls[i] && f.type.startsWith("image/") ? <img src={urls[i]} alt="" /> : <span>{fileExt(f.name)}</span>}
              </div>
            ))}
          </div>
        )}
        {tooBig && <p class="error pad">{tooBig.name} is larger than 100 MB.</p>}
        <div class="media-send-bar">
          <input
            autoFocus
            value={caption}
            onInput={(e) => setCaption(e.currentTarget.value)}
            onKeyDown={(e) => e.key === "Enter" && !busy && !tooBig && void send()}
            placeholder="Add a caption"
            aria-label="Caption"
          />
          {anyImage && (
            <button class={"view-once-toggle hd" + (hd ? " on" : "")} onClick={() => setHd(!hd)} aria-pressed={hd} aria-label="HD quality" title={hd ? "HD: sends photos at full quality" : "Standard quality (smaller)"}>
              HD
            </button>
          )}
          {files.length === 1 && (first.type.startsWith("image/") || first.type.startsWith("video/")) && (
            <button
              class={"view-once-toggle" + (viewOnce ? " on" : "")}
              onClick={() => setViewOnce(!viewOnce)}
              aria-pressed={viewOnce}
              aria-label="View once"
              title={viewOnce ? "View once: on" : "View once: off"}
            >
              1
            </button>
          )}
          <button class="icon-btn send-fab" disabled={busy || !!tooBig} onClick={() => void send()} aria-label="Send">
            <Icon name="send" />
          </button>
        </div>
      </div>
    </div>
  );
}

/** Used by search and starred lists. */
export function MessageResult({ m, onOpen }: { m: import("../db").Msg; onOpen: () => void }) {
  const who = m.dir === "out" ? "You" : S.displayName(m.from);
  return (
    <li class="msg-result" tabIndex={0} onClick={onOpen} onKeyDown={(e) => e.key === "Enter" && onOpen()}>
      <div class="msg-result-top">
        <span class="chat-name">{S.displayName(m.chat) === who ? who : `${who} → ${S.displayName(m.chat)}`}</span>
        <small>{new Date(m.ts).toLocaleDateString()}</small>
      </div>
      <div class="chat-preview">{summarize(m)}</div>
    </li>
  );
}

